'use client';

/**
 * The contents the AnswerDock shell carries, per shape. Presentational only;
 * PromptingPhaseScreen decides which one is showing.
 */

import { formatDuration } from '@core/components/ui/RecordButton';
import { LiveWaveform } from '@core/components/ui/LiveWaveform';
import { Waveform } from '@core/components/ui/Waveform';
import type { AudioLevelStore } from '@core/lib/audioLevelStore';

/** A quiet text action inside the dock. Literal 44px: rem would shrink it below 390px. */
export const DOCK_TEXT_ACTION =
  'inline-flex min-h-[44px] shrink-0 items-center rounded-[var(--radius-sm)] px-3 font-body text-[14px] text-text-secondary transition-colors cursor-pointer hover:text-accent-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2';

interface SpeakStatusProps {
  /** The status line: "Tap to speak", "Connecting...", "Listening", and so on. */
  hint: string;
  /** A second, quieter line under the status (a scale prompt). Not while listening. */
  detail?: string | null;
  /**
   * Listening: the status centres under a balanced bell meter, with the time
   * beside it ("Listening · 0:12").
   */
  listening?: {
    /** Live levels, once the microphone is capturing; absent while it starts. */
    levelStore?: AudioLevelStore;
    durationMs: number;
  };
}

/**
 * The status that lives inside the dock, beside the button the member touched.
 * `record-hint` stays one persistent polite live region whose text changes, so
 * every state change is announced exactly once: the meter row is inserted
 * above it, never around it, so it is the same element ready and listening.
 */
export function SpeakStatus({ hint, detail, listening }: SpeakStatusProps) {
  return (
    // While listening, pl-3 evens the two sides of the meter: the Stop button
    // and the shell's gap on the left, "Start over" and the content gap on the
    // right, so the meter sits on the dock's centre line at either root size.
    <div className={`min-w-0 flex-1 ${listening ? 'flex flex-col items-center pl-3 text-center' : ''}`}>
      {listening && (
        <span aria-hidden="true" data-dock-meter className="mb-1 flex justify-center">
          {listening.levelStore ? (
            <LiveWaveform store={listening.levelStore} isActive tone="accent" shape="bell" />
          ) : (
            // The microphone is still starting: the same bell, at rest.
            <Waveform levels={[]} isActive={false} tone="accent" shape="bell" />
          )}
        </span>
      )}
      <div className={`flex gap-2 ${listening ? 'items-baseline justify-center' : 'items-center'}`}>
        <span
          data-testid="record-hint"
          aria-live="polite"
          // Focus target of last resort for the text→voice handoff, when the
          // record button is disabled and this line says why. Never a tab stop.
          tabIndex={-1}
          className={`font-body font-medium leading-snug text-text-primary focus:outline-none ${
            listening ? 'text-[14px]' : 'text-[15px]'
          }`}
        >
          {hint}
        </span>
        {/* The time starts with the capture: while the mic is still starting,
            "Starting your mic..." stands alone on one line. */}
        {listening?.levelStore && (
          <span
            data-dock-duration
            className="font-body text-[14px] tabular-nums leading-snug text-text-secondary"
          >
            · {formatDuration(listening.durationMs)}
          </span>
        )}
      </div>
      {!listening && detail && (
        <p className="mt-0.5 text-pretty font-body text-[13px] leading-snug text-text-secondary">{detail}</p>
      )}
    </div>
  );
}

/**
 * The resting disc's thinking mark: a seven-node network whose links and nodes
 * fire in a slow sweep around a gently beating core (owner ask 2026-10-06:
 * "keeps it more interesting", and "VERY light weight").
 *
 * Deliberately cheap: one static inline SVG (18 elements) and CSS keyframes on
 * opacity, stroke opacity and transform (globals.css, `neural-*`): no layout,
 * just a repaint of a 28px box. No canvas, no requestAnimationFrame, no React
 * state, nothing re-renders; the browser throttles it in a hidden tab.
 * Negative delays start every element mid-cycle, so it never sits blank. It
 * fades in after a beat, so the short rest while a question or reflection lands
 * barely shows it. Under reduced motion the global rule ends each animation at
 * once and the network stays still at its base opacity.
 */
const NEURAL_CYCLE_S = 2.4;
const NEURAL_CORE = { x: 12, y: 12 };
/** Clockwise from the top left; each fires one sixth of a cycle after the last. */
const NEURAL_RING = [
  { x: 5.5, y: 7.5 },
  { x: 13.5, y: 4.5 },
  { x: 19.5, y: 9.5 },
  { x: 18, y: 17.5 },
  { x: 10.5, y: 19.5 },
  { x: 4.5, y: 14.5 },
] as const;
/** Ring links that fire as the sweep passes between their two nodes. */
const NEURAL_RING_LINKS = [
  [0, 1],
  [2, 3],
  [4, 5],
] as const;

/** A negative delay that places an element `at` seconds into the cycle. */
const neuralDelay = (at: number) => ({ animationDelay: `${(at - NEURAL_CYCLE_S).toFixed(2)}s` });
const fireAt = (i: number) => (i * NEURAL_CYCLE_S) / NEURAL_RING.length;

export function NeuralMark() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="28"
      height="28"
      aria-hidden="true"
      data-neural-mark
      className="text-accent animate-[neural-in_400ms_ease-out_200ms_both]"
    >
      <g stroke="currentColor" strokeWidth="1" strokeLinecap="round">
        {NEURAL_RING.map((node, i) => (
          <line
            key={`spoke-${i}`}
            x1={NEURAL_CORE.x}
            y1={NEURAL_CORE.y}
            x2={node.x}
            y2={node.y}
            strokeOpacity="0.25"
            className="animate-[neural-link_2.4s_cubic-bezier(0.25,1,0.5,1)_infinite]"
            style={neuralDelay(fireAt(i))}
          />
        ))}
        {NEURAL_RING_LINKS.map(([a, b]) => (
          <line
            key={`ring-${a}-${b}`}
            x1={NEURAL_RING[a].x}
            y1={NEURAL_RING[a].y}
            x2={NEURAL_RING[b].x}
            y2={NEURAL_RING[b].y}
            strokeOpacity="0.25"
            className="animate-[neural-link_2.4s_cubic-bezier(0.25,1,0.5,1)_infinite]"
            style={neuralDelay(fireAt(a) + 0.2)}
          />
        ))}
      </g>
      <g fill="currentColor">
        {NEURAL_RING.map((node, i) => (
          <circle
            key={`node-${i}`}
            cx={node.x}
            cy={node.y}
            r="1.25"
            opacity="0.6"
            className="origin-center [transform-box:fill-box] animate-[neural-node_2.4s_cubic-bezier(0.25,1,0.5,1)_infinite]"
            style={neuralDelay(fireAt(i) + 0.12)}
          />
        ))}
        <circle
          cx={NEURAL_CORE.x}
          cy={NEURAL_CORE.y}
          r="1.8"
          opacity="0.8"
          className="origin-center [transform-box:fill-box] animate-[neural-core_1.2s_ease-in-out_infinite]"
        />
      </g>
    </svg>
  );
}
